import {
  Vec2,
  deg2rad,
  entityBBox,
  rotateEntity,
  scaleEntity,
  translateEntity,
  withNewId,
} from '@lakaran/geometry';
import { Transaction } from '@lakaran/engine';
import { Tool, type ToolField } from './tool';

export class EraseTool extends Tool {
  readonly name = 'ERASE';
  readonly title = 'Erase';
  readonly aliases = ['erase', 'e', 'delete'];

  protected begin(): void {
    if (this.host.editor.selection.size > 0) {
      this.host.editor.deleteSelection();
      this.host.endTool();
      return;
    }
    this.host.prompt('ERASE  Select objects to erase [Enter to finish]:');
  }

  point(p: Vec2): void {
    const id = this.host.editor.pickAt(p);
    if (!id) return;
    const entity = this.host.doc.get(id);
    if (!entity) return;
    this.host.editor.commit(new Transaction().remove(entity), 'ERASE');
  }
}

export class MoveTool extends Tool {
  readonly name = 'MOVE';
  readonly title = 'Move';
  readonly aliases = ['move', 'm'];
  private base?: Vec2;
  private selecting = false;

  protected begin(): void {
    if (this.host.editor.selection.size === 0) {
      this.selecting = true;
      this.host.prompt('MOVE  Select objects [Enter to continue]:');
    } else {
      this.host.prompt('MOVE  Specify base point:');
    }
  }

  override get referencePoint(): Vec2 | undefined {
    return this.base;
  }

  point(p: Vec2): void {
    if (this.selecting) {
      const id = this.host.editor.pickAt(p);
      if (id) this.host.editor.toggleSelection(id);
      return;
    }
    if (!this.base) {
      this.base = p;
      this.host.setReference(p);
      this.host.prompt('MOVE  Specify destination point:');
      return;
    }
    const delta = p.sub(this.base);
    const tx = new Transaction();
    for (const entity of this.host.editor.selectedEntities()) {
      tx.update(entity, translateEntity(entity, delta));
    }
    this.host.editor.commit(tx, 'MOVE');
    this.host.endTool();
  }

  override enter(): void {
    if (this.selecting) {
      this.selecting = false;
      this.host.prompt('MOVE  Specify base point:');
      return;
    }
    this.host.endTool();
  }

  override hover(p: Vec2): void {
    if (!this.base) return;
    const delta = p.sub(this.base);
    this.host.setDraft(this.host.editor.selectedEntities().map((e) => translateEntity(e, delta)));
  }

  override getFields(cursor: Vec2): ToolField[] | null {
    if (!this.base) return null;
    return [
      { id: 'dx', label: 'ΔX', unit: 'mm', value: cursor.x - this.base.x, decimals: 3 },
      { id: 'dy', label: 'ΔY', unit: 'mm', value: cursor.y - this.base.y, decimals: 3 },
    ];
  }

  override applyFields(values: Record<string, number>): void {
    if (!this.base) return;
    this.point(this.base.add(Vec2.of(values['dx'] ?? 0, values['dy'] ?? 0)));
  }

  override cancel(): void {
    this.base = undefined;
    this.selecting = false;
    super.cancel();
  }
}

export class CopyTool extends Tool {
  readonly name = 'COPY';
  readonly title = 'Copy';
  readonly aliases = ['copy', 'co'];
  private base?: Vec2;
  private selecting = false;

  protected begin(): void {
    if (this.host.editor.selection.size === 0) {
      this.selecting = true;
      this.host.prompt('COPY  Select objects [Enter to continue]:');
    } else {
      this.host.prompt('COPY  Specify base point:');
    }
  }

  override get referencePoint(): Vec2 | undefined {
    return this.base;
  }

  point(p: Vec2): void {
    if (this.selecting) {
      const id = this.host.editor.pickAt(p);
      if (id) this.host.editor.toggleSelection(id);
      return;
    }
    if (!this.base) {
      this.base = p;
      this.host.setReference(p);
      this.host.prompt('COPY  Specify destination point:');
      return;
    }
    const delta = p.sub(this.base);
    const tx = new Transaction();
    for (const entity of this.host.editor.selectedEntities()) {
      tx.add(translateEntity(withNewId(entity), delta));
    }
    if (tx.isEmpty) {
      this.host.editor.message('COPY  Nothing selected.');
    } else {
      this.host.editor.commit(tx, 'COPY');
    }
    this.host.endTool();
  }

  override enter(): void {
    if (this.selecting) {
      this.selecting = false;
      this.host.prompt('COPY  Specify base point:');
      return;
    }
    this.host.endTool();
  }

  override hover(p: Vec2): void {
    if (!this.base) return;
    const delta = p.sub(this.base);
    this.host.setDraft(
      this.host.editor.selectedEntities().map((e) => translateEntity(withNewId(e), delta)),
    );
  }

  override getFields(cursor: Vec2): ToolField[] | null {
    if (!this.base) return null;
    return [
      { id: 'dx', label: 'ΔX', unit: 'mm', value: cursor.x - this.base.x, decimals: 3 },
      { id: 'dy', label: 'ΔY', unit: 'mm', value: cursor.y - this.base.y, decimals: 3 },
    ];
  }

  override applyFields(values: Record<string, number>): void {
    if (!this.base) return;
    this.point(this.base.add(Vec2.of(values['dx'] ?? 0, values['dy'] ?? 0)));
  }

  override cancel(): void {
    this.base = undefined;
    this.selecting = false;
    super.cancel();
  }
}

export class RotateTool extends Tool {
  readonly name = 'ROTATE';
  readonly title = 'Rotate';
  readonly aliases = ['rotate', 'ro'];
  private base?: Vec2;
  private selecting = false;

  protected begin(): void {
    if (this.host.editor.selection.size === 0) {
      this.selecting = true;
      this.host.prompt('ROTATE  Select objects [Enter to continue]:');
    } else {
      this.host.prompt('ROTATE  Specify base point:');
    }
  }

  override get referencePoint(): Vec2 | undefined {
    return this.base;
  }

  point(p: Vec2): void {
    if (this.selecting) {
      const id = this.host.editor.pickAt(p);
      if (id) this.host.editor.toggleSelection(id);
      return;
    }
    if (!this.base) {
      this.base = p;
      this.host.setReference(p);
      this.host.prompt('ROTATE  Specify rotation angle:');
      return;
    }
    this.commit(this.angleAt(p));
  }

  override enter(): void {
    if (this.selecting) {
      this.selecting = false;
      this.host.prompt('ROTATE  Specify base point:');
      return;
    }
    this.host.endTool();
  }

  override hover(p: Vec2): void {
    if (!this.base) return;
    const angle = deg2rad(this.angleAt(p));
    this.host.setDraft(
      this.host.editor.selectedEntities().map((e) => rotateEntity(e, this.base!, angle)),
    );
  }

  override getFields(cursor: Vec2): ToolField[] | null {
    if (!this.base) return null;
    return [{ id: 'angle', label: 'Angle', unit: 'deg', value: this.angleAt(cursor), decimals: 2 }];
  }

  override applyFields(values: Record<string, number>): void {
    this.commit(values['angle'] ?? 0);
  }

  override cancel(): void {
    this.base = undefined;
    this.selecting = false;
    super.cancel();
  }

  private angleAt(p: Vec2): number {
    if (!this.base) return 0;
    const degrees = (Math.atan2(p.y - this.base.y, p.x - this.base.x) * 180) / Math.PI;
    return Math.round(degrees * 1e6) / 1e6;
  }

  private commit(angleDegrees: number): void {
    if (!this.base) return;
    const tx = new Transaction();
    for (const entity of this.host.editor.selectedEntities()) {
      tx.update(entity, rotateEntity(entity, this.base, deg2rad(angleDegrees)));
    }
    if (!tx.isEmpty) this.host.editor.commit(tx, 'ROTATE');
    this.host.endTool();
  }
}

export class ScaleTool extends Tool {
  readonly name = 'SCALE';
  readonly title = 'Scale';
  readonly aliases = ['scale', 'sc'];
  private base?: Vec2;
  private selecting = false;
  private reference = 1;

  protected begin(): void {
    if (this.host.editor.selection.size === 0) {
      this.selecting = true;
      this.host.prompt('SCALE  Select objects [Enter to continue]:');
    } else {
      this.host.prompt('SCALE  Specify base point:');
    }
  }

  override get referencePoint(): Vec2 | undefined {
    return this.base;
  }

  /** The scale factor is a distance ratio, so a 45° cursor constraint would distort it. */
  override get supportsAngleConstraint(): boolean {
    return false;
  }

  point(p: Vec2): void {
    if (this.selecting) {
      const id = this.host.editor.pickAt(p);
      if (id) this.host.editor.toggleSelection(id);
      return;
    }
    if (!this.base) {
      this.base = p;
      this.reference = this.selectionExtentFrom(p);
      this.host.setReference(p);
      this.host.prompt('SCALE  Specify scale factor:');
      return;
    }
    this.commit(this.base.distanceTo(p) / this.reference);
  }

  override enter(): void {
    if (this.selecting) {
      this.selecting = false;
      this.host.prompt('SCALE  Specify base point:');
      return;
    }
    this.host.endTool();
  }

  override hover(p: Vec2): void {
    if (!this.base) return;
    const factor = this.factorAt(p);
    this.host.setDraft(
      this.host.editor.selectedEntities().map((e) => scaleEntity(e, this.base!, factor)),
    );
  }

  override getFields(cursor: Vec2): ToolField[] | null {
    if (!this.base) return null;
    return [
      {
        id: 'factor',
        label: 'Factor',
        unit: 'num',
        value: this.factorAt(cursor),
        decimals: 4,
        min: 0.0001,
      },
    ];
  }

  override applyFields(values: Record<string, number>): void {
    this.commit(values['factor'] ?? 1);
  }

  override cancel(): void {
    this.base = undefined;
    this.selecting = false;
    super.cancel();
  }

  private factorAt(p: Vec2): number {
    if (!this.base) return 1;
    return this.base.distanceTo(p) / this.reference;
  }

  private selectionExtentFrom(base: Vec2): number {
    let extent = 0;
    for (const entity of this.host.editor.selectedEntities()) {
      for (const corner of entityBBox(entity).corners()) {
        extent = Math.max(extent, base.distanceTo(corner));
      }
    }
    return Math.max(extent, 1e-6);
  }

  private commit(factor: number): void {
    if (!this.base || !(factor > 0)) {
      this.host.editor.message('SCALE  Factor must be greater than zero.');
      return;
    }
    const tx = new Transaction();
    for (const entity of this.host.editor.selectedEntities()) {
      tx.update(entity, scaleEntity(entity, this.base, factor));
    }
    if (!tx.isEmpty) this.host.editor.commit(tx, 'SCALE');
    this.host.endTool();
  }
}

export function createModifyTools(): Array<() => Tool> {
  return [
    () => new EraseTool(),
    () => new MoveTool(),
    () => new CopyTool(),
    () => new RotateTool(),
    () => new ScaleTool(),
  ];
}
